import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

async function apiPost(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

function MetricCell({ value, isClient, isLoading }) {
  if (isLoading) return <td style={{ textAlign: 'center', color: 'var(--gray-300)' }}>…</td>;
  const num = typeof value === 'number' ? value.toLocaleString() : (value || '—');
  return (
    <td style={{
      textAlign: 'center',
      fontWeight: isClient ? 700 : 400,
      color: isClient ? 'var(--blue-mid)' : 'var(--gray-800)',
      fontSize: 12,
    }}>
      {num}
    </td>
  );
}

function TextCell({ value, isClient, maxWidth = 200 }) {
  if (!value) return <td style={{ color: 'var(--gray-400)', fontSize: 12 }}>—</td>;
  return (
    <td style={{
      fontSize: 12,
      color: isClient ? 'var(--blue-mid)' : 'var(--gray-800)',
      fontWeight: isClient ? 600 : 400,
      maxWidth,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }} title={value}>
      {value}
    </td>
  );
}

function KeywordTable({ keywordData, clientUrl }) {
  const { keyword, rows, status } = keywordData;
  const clientDomain = clientUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];

  // Averages across competitor rows only (not client)
  const competitorRows = rows.filter(r => !r.isClient && !r.loading);
  const avg = (field) => {
    if (!competitorRows.length) return '—';
    const vals = competitorRows.map(r => parseFloat(r[field]) || 0);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    return mean % 1 === 0 ? mean.toLocaleString() : mean.toFixed(1);
  };
  const avgText = (field, maxLen = 40) => {
    if (!competitorRows.length) return '—';
    const vals = competitorRows.map(r => (r[field] || '').length);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    return `Avg. ${Math.round(mean)} chars`;
  };

  return (
    <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', background: 'var(--blue-mid)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>{keyword}</div>
        <span style={{ fontSize: 12, color: '#93C5FD' }}>
          {status === 'complete' ? `${rows.filter(r => !r.isClient).length} competitors analysed` : status}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' }}>Pos</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', minWidth: 200 }}>URL</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', minWidth: 180 }}>Title Tag</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', minWidth: 160 }}>H1</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', minWidth: 180 }}>Meta Description</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' }}>Backlinks</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' }}>Ref. Domains</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' }}>Page Rank</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' }}>Spam Score</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' }}>Int. Links</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' }}>Ext. Links</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const isClient = row.isClient;
              const isLoading = row.loading;
              const bg = isClient ? '#EFF6FF' : i % 2 === 0 ? '#fff' : '#F9FAFB';
              return (
                <tr key={i} style={{ background: bg, borderBottom: isClient ? '2px solid #185FA5' : '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: isClient ? 700 : 400, color: isClient ? 'var(--blue-mid)' : 'var(--gray-600)', textAlign: 'center' }}>
                    {isClient ? 'Client' : row.position}
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <a href={row.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: isClient ? 'var(--blue-mid)' : 'var(--blue)', textDecoration: 'none', fontWeight: isClient ? 700 : 400 }} title={row.url}>
                      {row.url?.replace(/^https?:\/\//, '').substring(0, 40)}{row.url?.length > 40 ? '…' : ''}
                    </a>
                  </td>
                  {isLoading ? (
                    <td colSpan={9} style={{ padding: '8px 12px', color: 'var(--gray-400)', fontSize: 12, textAlign: 'center' }}>
                      <span className="spinner" style={{ marginRight: 6 }} />Fetching on-page data & backlinks…
                    </td>
                  ) : (
                    <>
                      <TextCell value={row.title} isClient={isClient} />
                      <TextCell value={row.h1} isClient={isClient} />
                      <TextCell value={row.metaDescription} isClient={isClient} maxWidth={220} />
                      <MetricCell value={row.totalBacklinks} isClient={isClient} />
                      <MetricCell value={row.referringDomains} isClient={isClient} />
                      <MetricCell value={row.rank} isClient={isClient} />
                      <MetricCell value={row.spamScore} isClient={isClient} />
                      <MetricCell value={row.internalLinks} isClient={isClient} />
                      <MetricCell value={row.externalLinks} isClient={isClient} />
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
          {competitorRows.length > 0 && (
            <tfoot>
              <tr style={{ background: '#1A2744' }}>
                <td style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#93C5FD', textTransform: 'uppercase', letterSpacing: '0.04em' }} colSpan={2}>
                  Top 5 average
                </td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: '#CBD5E1' }}>{avgText('title')}</td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: '#CBD5E1' }}>{avgText('h1')}</td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: '#CBD5E1' }}>{avgText('metaDescription')}</td>
                {['totalBacklinks','referringDomains','rank','spamScore','internalLinks','externalLinks'].map(field => (
                  <td key={field} style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#93C5FD', textAlign: 'center' }}>
                    {avg(field)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export default function CompetitiveAnalysis({ onBack }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [locationCode, setLocationCode] = useState(2826);
  const [rows, setRows] = useState([
    { keyword: '', clientUrl: '' },
    { keyword: '', clientUrl: '' },
    { keyword: '', clientUrl: '' },
    { keyword: '', clientUrl: '' },
    { keyword: '', clientUrl: '' },
  ]);
  const [running, setRunning] = useState(false);
  const [keywordResults, setKeywordResults] = useState([]);
  const [log, setLog] = useState([]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('clients').select('id, name, url').order('name')
      .then(({ data }) => { if (data) setClients(data); });
  }, []);

  const addLog = (msg, type = 'info') => {
    setLog(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
  };

  const updateKeywordRows = (kwIndex, updater) => {
    setKeywordResults(prev => prev.map((kd, i) => i === kwIndex ? { ...kd, rows: updater(kd.rows) } : kd));
  };

  const handleRun = async () => {
    const activeRows = rows.filter(r => r.keyword.trim() && r.clientUrl.trim());
    if (!activeRows.length) { setError('Enter at least one keyword with a client URL.'); return; }

    setRunning(true);
    setDone(false);
    setError('');
    setLog([]);
    setKeywordResults([]);

    // Initialise result slots
    setKeywordResults(activeRows.map(r => ({ keyword: r.keyword, clientUrl: r.clientUrl, rows: [], status: 'Queued…' })));

    for (let ki = 0; ki < activeRows.length; ki++) {
      const keyword = activeRows[ki].keyword;
      const cleanClientUrl = activeRows[ki].clientUrl.trim().startsWith('http') ? activeRows[ki].clientUrl.trim() : `https://${activeRows[ki].clientUrl.trim()}`;
      addLog(`Starting analysis for "${keyword}"…`);

      // Update status
      setKeywordResults(prev => prev.map((kd, i) => i === ki ? { ...kd, status: 'Fetching SERP…' } : kd));

      // ── 1. SERP ─────────────────────────────────────────────
      addLog(`Fetching top 5 Google results for "${keyword}"…`);
      let serpResults = [];
      try {
        const serpData = await apiPost('/api/competitive/serp', { keyword, locationCode, depth: 5 });
        serpResults = serpData.results || [];
        addLog(`Found ${serpResults.length} competitors for "${keyword}"`, 'success');
      } catch (e) {
        addLog(`SERP failed for "${keyword}"`, 'error');
      }

      // Add client URL as a row too
      const allUrls = [
        ...serpResults.map(r => ({ ...r, isClient: false, loading: true })),
        { url: cleanClientUrl, domain: cleanClientUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0], position: null, title: '', isClient: true, loading: true },
      ];

      setKeywordResults(prev => prev.map((kd, i) => i === ki ? { ...kd, rows: allUrls, status: 'Crawling pages…' } : kd));

      // ── 2. OnPage + Backlinks — all URLs in parallel ────────
      addLog(`Crawling all ${allUrls.length} URLs in parallel…`);

      await Promise.all(allUrls.map(async (urlData, ui) => {
        try {
          const [onpage, backlinks] = await Promise.all([
            apiPost('/api/competitive/onpage', { url: urlData.url }),
            apiPost('/api/competitive/backlinks', { url: urlData.url }),
          ]);

          const enriched = {
            ...urlData,
            title: onpage.title || urlData.title || '',
            h1: onpage.h1 || '',
            metaDescription: onpage.metaDescription || urlData.description || '',
            wordCount: onpage.wordCount || 0,
            internalLinks: onpage.internalLinks || 0,
            externalLinks: onpage.externalLinks || 0,
            totalBacklinks: backlinks.totalBacklinks || 0,
            referringDomains: backlinks.referringDomains || 0,
            referringPages: backlinks.referringPages || 0,
            rank: backlinks.rank || 0,
            spamScore: backlinks.spamScore || 0,
            loading: false,
          };

          setKeywordResults(prev => prev.map((kd, i) => {
            if (i !== ki) return kd;
            const newRows = kd.rows.map((r, ri) => ri === ui ? enriched : r);
            return { ...kd, rows: newRows };
          }));

          addLog(`✓ ${urlData.isClient ? 'Client' : `#${urlData.position}`} done — ${backlinks.referringDomains?.toLocaleString() || 0} ref. domains`, 'success');
        } catch (e) {
          addLog(`✗ Failed: ${urlData.url?.substring(0, 50)}`, 'error');
          // Mark as done even on error so row stops loading
          setKeywordResults(prev => prev.map((kd, i) => {
            if (i !== ki) return kd;
            const newRows = kd.rows.map((r, ri) => ri === ui ? { ...r, loading: false } : r);
            return { ...kd, rows: newRows };
          }));
        }
      }));

      setKeywordResults(prev => prev.map((kd, i) => i === ki ? { ...kd, status: 'complete' } : kd));
      addLog(`"${keyword}" complete`, 'success');
    }

    addLog('All keywords analysed!', 'success');
    setRunning(false);
    setDone(true);
  };

  const [exporting, setExporting] = useState(false);
  const [generatingReview, setGeneratingReview] = useState(false);
  const [review, setReview] = useState('');
  const [showReview, setShowReview] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/export/competitive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keywordResults, clientName: keywordResults[0]?.clientUrl || '' }),
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IMSR-competitive-analysis-${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };

  const generateReview = async () => {
    setGeneratingReview(true);
    setShowReview(true);
    // Resolve client name — use saved client name if selected, otherwise derive from URL
    const selectedClient = clients.find(c => c.id === clientId);
    const resolvedClientName = selectedClient?.name ||
      rows.filter(r => r.keyword.trim())[0]?.clientUrl?.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] ||
      'the client';
    const resolvedClientUrl = rows.filter(r => r.keyword.trim())[0]?.clientUrl || '';
    try {
      const res = await fetch(`${API_URL}/api/content/competitive-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywordResults,
          clientName: resolvedClientName,
          clientUrl: resolvedClientUrl,
        }),
      });
      const data = await res.json();
      setReview(data.review || '');
    } catch (e) {
      console.error('Review generation failed:', e);
    }
    setGeneratingReview(false);
  };

  const downloadReviewDoc = async () => {
    const res = await fetch(`${API_URL}/api/content/export-doc`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content: review,
        metaTitle: 'Competitive Analysis Review',
        metaDescription: '',
        keyword: 'Competitive Analysis',
        businessName: rows.filter(r => r.keyword.trim())[0]?.clientUrl || 'Client',
        imageIdeas: [],
      }),
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `competitive-analysis-review-${new Date().toISOString().slice(0,10)}.docx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Input screen ─────────────────────────────────────────────
  if (!running && !done) return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">Competitive Analysis</div>
          <div className="page-sub">Compare your client against top Google competitors for any keyword</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Client details</div>
        <div className="form-row form-row-2 form-group">
          <div>
            <label>Link to client (optional)</label>
            <select value={clientId} onChange={e => {
              setClientId(e.target.value);
              const c = clients.find(c => c.id === e.target.value);
              if (c?.url) setRows(prev => prev.map(r => ({ ...r, clientUrl: r.clientUrl || c.url })));
            }}>
              <option value="">— Select a client —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Country</label>
          <select value={locationCode} onChange={e => setLocationCode(parseInt(e.target.value))} style={{ maxWidth: 260 }}>
            <option value={2826}>United Kingdom</option>
            <option value={2840}>United States</option>
            <option value={2036}>Australia</option>
            <option value={2124}>Canada</option>
            <option value={2372}>Ireland</option>
          </select>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Keywords to analyse</div>
        <div className="card-sub">Enter up to 5 keywords, each with the specific client URL to compare for that keyword.</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 4px' }}>Keyword</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 4px' }}>Client URL to compare</div>
        </div>

        {rows.map((row, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr 1fr', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: row.keyword.trim() ? 'var(--blue-mid)' : 'var(--gray-200)', color: row.keyword.trim() ? '#fff' : 'var(--gray-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
            <input type="text" placeholder={`Keyword ${i + 1}${i === 0 ? ' (required)' : ' (optional)'}`} value={row.keyword} onChange={e => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, keyword: e.target.value } : r))} />
            <input type="url" placeholder="https://clientsite.co.uk/page/" value={row.clientUrl} onChange={e => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, clientUrl: e.target.value } : r))} />
          </div>
        ))}
        <div className="alert alert-info" style={{ marginTop: 8 }}>
          Each keyword runs: SERP fetch → on-page crawl (6 URLs) → backlinks check (6 URLs). Expect 1–3 minutes per keyword.
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn btn-primary" onClick={handleRun} disabled={!rows.filter(r => r.keyword.trim() && r.clientUrl.trim()).length}>
          Run analysis for {rows.filter(r => r.keyword.trim() && r.clientUrl.trim()).length} keyword{rows.filter(r => r.keyword.trim() && r.clientUrl.trim()).length !== 1 ? 's' : ''} →
        </button>
      </div>
    </div>
  );

  // ── Results screen ───────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-sm" onClick={() => { setDone(false); setRunning(false); setKeywordResults([]); setLog([]); }} disabled={running}>
            ← New analysis
          </button>
          <div>
            <div className="page-title">Competitive Analysis</div>
            <div className="page-sub">{rows.filter(r=>r.keyword.trim()).map(r=>r.keyword).join(', ')}</div>
          </div>
        </div>
        {done && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={exportExcel} disabled={exporting}>
              {exporting ? <><span className="spinner" /> Generating…</> : '↓ Download Excel'}
            </button>
            <button className="btn" onClick={generateReview} disabled={generatingReview} style={{ background: '#1A2744', color: '#fff', borderColor: '#1A2744' }}>
              {generatingReview ? <><span className="spinner" /> Writing review…</> : '✍️ Generate written review'}
            </button>
          </div>
        )}
      </div>

      {/* Live log */}
      <div className="card" style={{ marginBottom: 16, maxHeight: 180, overflowY: 'auto', background: '#0F172A' }}>
        {log.map((entry, i) => (
          <div key={i} style={{ fontSize: 12, fontFamily: 'monospace', padding: '2px 4px', color: entry.type === 'success' ? '#86EFAC' : entry.type === 'error' ? '#FCA5A5' : '#94A3B8' }}>
            <span style={{ color: '#475569', marginRight: 8 }}>{entry.time}</span>
            {entry.msg}
          </div>
        ))}
        {running && <div style={{ fontSize: 12, fontFamily: 'monospace', padding: '2px 4px', color: '#60A5FA' }}><span className="spinner" style={{ width: 10, height: 10, marginRight: 6 }} />Working…</div>}
      </div>

      {/* Results tables */}
      {keywordResults.map((kd, i) => (
        <KeywordTable key={i} keywordData={kd} clientUrl={kd.clientUrl || ''} />
      ))}
      {/* Written review panel */}
      {showReview && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Written Review</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={() => navigator.clipboard.writeText(review)}>Copy</button>
              <button className="btn btn-sm" onClick={downloadReviewDoc} disabled={!review}>↓ Word doc</button>
              <button className="btn btn-sm" onClick={generateReview} disabled={generatingReview}>↻ Regenerate</button>
            </div>
          </div>
          {generatingReview ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2rem', color: 'var(--gray-500)', fontSize: 13 }}>
              <span className="spinner" /> Writing competitive analysis review…
            </div>
          ) : (
            <textarea
              value={review}
              onChange={e => setReview(e.target.value)}
              rows={24}
              style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.7, border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-md)', padding: '12px 14px', resize: 'vertical', color: 'var(--gray-900)' }}
            />
          )}
        </div>
      )}
    </div>
  );
}
